import { useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';

const previewFeatures = [
  {
    id: 'analytics',
    title: 'Conversational Analytics',
    shortTitle: 'Analytics',
    description: 'Track what customers ask most and understand how they feel in the moment.',
    src: '/home-page-imagery/Conversational%20Analytics.png',
    alt: 'Help Genie conversational analytics dashboard',
  },
  {
    id: 'genie-screen',
    title: 'Genie Configuration',
    shortTitle: 'Configuration',
    description: "Fine-tune every aspect of your genie's personality, voice, and behavior.",
    src: '/home-page-imagery/Genie%20Screen.png',
    alt: 'Help Genie configuration screen',
  },
  {
    id: 'conversations',
    title: 'My Conversations',
    shortTitle: 'Conversations',
    description: 'Review and analyze every customer interaction in one unified inbox.',
    src: '/home-page-imagery/My%20Conversations.png',
    alt: 'Help Genie conversations view',
  },
];

export const PlaybookPlatformPreview = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % previewFeatures.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isHovered]);

  useEffect(() => {
    if (tabsContainerRef.current) {
      const activeButton = tabsContainerRef.current.querySelector(
        `[data-index="${activeIndex}"]`
      ) as HTMLElement;
      if (activeButton) {
        const container = tabsContainerRef.current;
        const buttonLeft = activeButton.offsetLeft;
        const buttonWidth = activeButton.offsetWidth;
        const containerWidth = container.offsetWidth;
        const scrollLeft = buttonLeft - containerWidth / 2 + buttonWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [activeIndex]);

  return (
    <section className="w-full bg-gray-50 py-12 sm:py-16 md:py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <div className="h-1 w-16 bg-gradient-to-r from-primary to-secondary rounded-full mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900">
            The platform behind your{' '}
            <span className="bg-gradient-to-r from-[#4E9CFF] via-[#FF4DB8] to-[#FFBB33] bg-clip-text text-transparent">
              genie
            </span>
          </h2>
          <p className="mt-3 text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto px-2">
            Manage, monitor, and optimise your voice genie from a single dashboard.
          </p>
        </div>

        {/* Feature tabs */}
        <div
          ref={tabsContainerRef}
          className="flex md:flex-wrap md:justify-center gap-2 mb-6 sm:mb-8 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory md:snap-none"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {previewFeatures.map((feature, index) => (
            <button
              key={feature.id}
              data-index={index}
              onClick={() => setActiveIndex(index)}
              className={cn(
                'relative px-4 py-2.5 sm:py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 snap-center touch-manipulation min-h-[44px]',
                index === activeIndex
                  ? 'bg-gradient-to-r from-[#4E9CFF] to-[#28406F] text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
              )}
            >
              <span className="sm:hidden">{feature.shortTitle}</span>
              <span className="hidden sm:inline">{feature.title}</span>
              {index === activeIndex && !isHovered && (
                <span
                  className="absolute bottom-0 left-0 h-0.5 bg-white/50 animate-progress rounded-full"
                  style={{ animationDuration: '4s' }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Image display */}
        <div className="relative">
          {previewFeatures.map((feature, index) => (
            <div
              key={feature.id}
              className={`transition-opacity duration-500 ${
                index === activeIndex
                  ? 'opacity-100 relative'
                  : 'opacity-0 absolute inset-0 pointer-events-none'
              }`}
            >
              <img
                src={feature.src}
                alt={feature.alt}
                loading={index === 0 ? 'eager' : 'lazy'}
                className="w-full h-auto"
              />
            </div>
          ))}
        </div>

        {/* Description below image */}
        <p className="mt-4 text-center text-gray-600 text-xs sm:text-sm lg:text-base px-2">
          {previewFeatures[activeIndex].description}
        </p>

        {/* Mobile swipe indicator */}
        <div className="flex justify-center gap-2 mt-4 md:hidden">
          {previewFeatures.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
              aria-label={`Go to feature ${index + 1}`}
            >
              <span
                className={cn(
                  'w-2 h-2 rounded-full transition-all',
                  index === activeIndex ? 'bg-primary scale-125' : 'bg-gray-300'
                )}
              />
            </button>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-progress {
          animation: progress linear forwards;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};
